import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { useLocation } from 'react-router-dom'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import { IconCLoseSquare } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { RadioButton } from 'components-ref/atoms'
import { useModalContext } from 'context/modalContext'
import { blockUser, reportUser, restrictUser } from 'services/endpoints/report'
import { useUserProfile } from 'store'

import style from './_block-user.module.scss'
import Report from '../Report/Report'

interface BlockUserProps {
    user: any
    successFn?: () => void
    clearModal?: boolean
    onClose?: () => void
    isSubscribedToUser?: boolean
}

const BlockUser = ({ user, successFn, clearModal = true, onClose, isSubscribedToUser = false}: BlockUserProps) => {
    const [activeButton, setActiveButton] = useState<string>('')

    const { t } = useTranslation()
    const modalData = useModalContext()
    const queryClient = useQueryClient()
    const userData = useUserProfile()
    const location = useLocation()
    const isSubscribed = user?.isSubscribed === true

    const blockedUserId = userData?.blocke_by_model?.map(
        (blockedUser: any) => blockedUser?.blocked_user_id
    )
    const removeBlockedUserFromCurrentGroupCache = (blockedId: number) => {
        if (!location.pathname.startsWith('/fans/')) return

        const groupSlug = location.pathname.replace(/^\/fans\//, '').split('/')[0]

        if (!groupSlug) return

        queryClient.setQueriesData<any>(
            ['fans-group', groupSlug],
            (oldData: { users: any[] }) => {
                if (!oldData) return oldData

                return {
                    ...oldData,
                    users: Array.isArray(oldData.users)
                        ? oldData.users.filter(
                              (cachedUser: any) => Number(cachedUser?.id) !== Number(blockedId)
                          )
                        : []
                }
            }
        )
    }

    const closeBlockUser = () => {
        if (onClose) {
            onClose()
            return
        }

        if (clearModal) modalData.clearModal()
    }

    const toggleButton = (type: string) => {
        setActiveButton(activeButton !== type ? type : '')
    }

    const toggleReportModal = () => {
        modalData.addModal(
            isSubscribed ? t('reportSubscriber') : t('reportUser'),
            <Report
                type='user'
                confirmFn={(message: string, reportType: number) => {
                    reportMutation.mutate({
                        report_type_id: reportType,
                        text: message
                    })
                }}
                closeFn={() => modalData.clearModal()}
            />
        )
    }

    const blockMutation = useMutation(async () => await blockUser(user.id), {
        onSuccess: () => {
            removeBlockedUserFromCurrentGroupCache(user.id)
            queryClient.invalidateQueries('fans-groups')

            if (successFn) successFn()

            closeBlockUser()
        },
        onError: () => {
            addToast('error', 'Something went wrong')
        }
    })
    const restrictMutation = useMutation(async () => await restrictUser(user.id), {
        onSuccess: () => {
            if (successFn) successFn()
            closeBlockUser()
        },
        onError: () => {
            addToast('error', 'Something went wrong')
        }
    })

    const reportMutation = useMutation(
        async (reportData: { text: string; report_type_id: number }) =>
            await reportUser({
                id: user.id,
                reportData
            }),
        {
            onSuccess: () => {
                addToast('success', 'User reported')

                if (successFn) successFn()

                closeBlockUser()
            },
            onError: () => {
                addToast('error', 'Something went wrong')
            }
        }
    )

    return (
        <div className={style.container}>
            <div className={style.user_details}>
                <div className={style.user_avatar}>
                    <img src={user?.avatar?.url || avatarPlaceholder} alt='avatar' />

<div className={style.user_avatar_icon_container}>
    <svg viewBox='0 0 24 24' fill='none'>
        <circle cx='12' cy='12' r='8' />
        <path d='M6.5 6.5L17.5 17.5' />
    </svg>
</div>
                </div>

                <div className={style.user_name}>
                    <div>{user?.display_name || ''}</div>
                    <div>@{user?.username || ''}</div>
                </div>
            </div>

<div className={style.options}>

    <div className={style.option}>
        <RadioButton
            active={activeButton === 'restrict'}
            clickFn={() => toggleButton('restrict')}
        />

        <div className={style.option_text}>
            <p>{t('restrictUser')}</p>

            <span className={style.option_subtext}>
                User will not be able to send you direct messages or reply to your posts.
            </span>
        </div>
    </div>
                    <div className={style.option}>
                    <RadioButton
                        active={activeButton === 'report'}
                        clickFn={() => toggleButton('report')}
                    />

                    <div className={style.option_text}>
                        <p>
                            {isSubscribed
                            ? t('reportSubscriber')
                            : t('reportUser')}

                        </p>
                    </div>
                </div>
                <div className={style.option_divider} />
                    <div className={style.option}>
        <RadioButton
            active={activeButton === 'block'}
            clickFn={() => toggleButton('block')}
        />

        <div className={style.option_text}>
            <p>
                {isSubscribed
                    ? t('blockSubscriber')
                    : t('blockUser')}
            </p>

            {userData?.role === 'fan' && (
                <span className={style.option_subtext}>
                    This creator will no longer be able to message you. You will be unsubscribed immediately. Refunds are not permissible.
                </span>
            )}

            {isSubscribed && (
                <span className={style.option_subtext}>
                    This option may result in a refund that would be deducted from your balance.
                </span>
            )}
        </div>
    </div>
</div>

            <div className={style.buttons}>
                <Button
                    color='grey'
                    text={t('close')}
                    clickFn={closeBlockUser}
                />

                <Button
                    color='black'
                    text={t('confirm')}
                    clickFn={() => {
                        if (blockedUserId?.includes(user?.id) && activeButton === 'block') {
                            addToast('error', 'Already blocked.')
                            return
                        }

                        if (activeButton === 'block') {
                            blockMutation.mutate()
                            return
                        }

                        if (activeButton === 'restrict') {
                            restrictMutation.mutate()
                            return
                        }

                        if (activeButton === 'report') {
                            toggleReportModal()
                        }
                    }}
                    disabled={activeButton === ''}
                />
            </div>
        </div>
    )
}

export default BlockUser
